import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Bell, AlertTriangle, CreditCard, Building2, Shield, Calendar, Wallet, UserCircle } from "lucide-react";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const typeIcons = {
  payment: CreditCard,
  tax: Building2,
  insurance: Shield,
  bagkur: Shield,
  leave: Calendar,
  salary: Wallet
};

const typeLabels = {
  payment: "Ödeme",
  tax: "Vergi",
  insurance: "Sigorta",
  bagkur: "Bağkur",
  leave: "İzin",
  salary: "Maaş"
};

const typeColors = {
  payment: "amber",
  tax: "rose",
  insurance: "sky",
  bagkur: "emerald",
  leave: "purple",
  salary: "orange"
};

const Alerts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error("Uyarılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const urgentAlerts = data?.alerts?.filter(a => a.urgent) || [];
  const normalAlerts = data?.alerts?.filter(a => !a.urgent) || [];

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Uyarılar & Hatırlatmalar</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Toplam {data?.total || 0} uyarı, {data?.urgent || 0} acil
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-rose-400/10 text-rose-400 rounded-sm text-sm">
            {data?.urgent || 0} Acil
          </span>
          <span className="px-3 py-1 bg-amber-400/10 text-amber-400 rounded-sm text-sm">
            {(data?.total || 0) - (data?.urgent || 0)} Normal
          </span>
        </div>
      </div>

      {/* Urgent Alerts */}
      {urgentAlerts.length > 0 && (
        <div className="bg-rose-400/10 border border-rose-400/20 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <h2 className="text-lg font-outfit font-medium text-rose-400">Acil Uyarılar</h2>
          </div>
          <div className="space-y-3">
            {urgentAlerts.map((alert, index) => {
              const Icon = typeIcons[alert.type] || Bell;
              const color = typeColors[alert.type] || "amber";
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 bg-zinc-950 border border-zinc-800 rounded-sm"
                  data-testid={`urgent-alert-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-sm bg-${color}-400/10`}>
                      <Icon className={`w-5 h-5 text-${color}-400`} />
                    </div>
                    <div>
                      <p className="text-zinc-50 font-medium">{alert.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded bg-${color}-400/10 text-${color}-400`}>
                          {typeLabels[alert.type] || alert.type}
                        </span>
                        {alert.subtype && (
                          <span className="text-xs text-zinc-500">{alert.subtype}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {alert.amount && (
                      <p className="text-lg font-mono text-rose-400">{formatCurrency(alert.amount)}</p>
                    )}
                    {alert.due_date && (
                      <p className="text-xs text-rose-400">
                        {alert.days_until === 0 ? 'BUGÜN!' : 
                         alert.days_until < 0 ? `${Math.abs(alert.days_until)} gün gecikti` :
                         `${alert.days_until} gün kaldı`}
                      </p>
                    )}
                    {alert.due_date && (
                      <p className="text-xs text-zinc-500 mt-1">{alert.due_date}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Normal Alerts */}
      {normalAlerts.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-outfit font-medium text-zinc-50">Diğer Uyarılar</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {normalAlerts.map((alert, index) => {
              const Icon = typeIcons[alert.type] || Bell;
              const color = typeColors[alert.type] || "amber";
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="flex items-center justify-between p-3 bg-zinc-950 border border-zinc-800 rounded-sm hover:bg-zinc-800/50 transition-colors"
                  data-testid={`normal-alert-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 text-${color}-400`} />
                    <div>
                      <p className="text-sm text-zinc-50">{alert.title}</p>
                      <span className="text-xs text-zinc-500">{typeLabels[alert.type] || alert.type}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {alert.amount && (
                      <p className="text-sm font-mono text-zinc-400">{formatCurrency(alert.amount)}</p>
                    )}
                    {alert.due_date && (
                      <p className="text-xs text-zinc-500">{alert.due_date}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Alerts */}
      {(!data?.alerts || data.alerts.length === 0) && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-emerald-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="text-lg font-outfit font-medium text-zinc-50">Tebrikler!</h3>
          <p className="text-zinc-500 mt-2">Şu anda bekleyen uyarı veya hatırlatma yok.</p>
        </div>
      )}
    </div>
  );
};

export default Alerts;
